import { MAX_GIFT_RECIPIENTS } from '@activities/gift/GiftOrderActivity/atoms/giftOrderAtoms';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import ArrowLineDown from '@icons/arrow/ArrowLineDown';
import ArrowLineUp from '@icons/arrow/ArrowLineUp';
import CloseInCircleIcon from '@icons/CloseInCircleIcon';
import { useFlow } from '@stackflow/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import useGiftRecipients from './hooks/useGiftRecipients';

function Receiver() {
    const { t } = useTranslation('gift');
    const { push } = useFlow();
    const {
        giftRecipients, isGiftToMe, deleteRecipient, toggleGiftToMe,
    } = useGiftRecipients();
    const [expanded, setExpanded] = useState(true);

    const handleToggleGiftToMe = () => {
        if (toggleGiftToMe()) setExpanded(true);
    };

    const handleAddContact = () => {
        if (giftRecipients.length >= MAX_GIFT_RECIPIENTS) {
            toast.error(t('giftOrder.recipientLimit', { count: MAX_GIFT_RECIPIENTS }));
            return;
        }
        push('ContactMethodBottomSheet', {});
    };

    return (
        <Column fullWidth spacing={2}>
            <HeaderRow fullWidth justify='space-between' align='center'>
                <Text fontWeight='bold' size='title'>
                    {t('giftOrder.receiverTitle')}
                </Text>
                <GiftToMeCheck>
                    <CheckInput type='checkbox' checked={isGiftToMe} onChange={handleToggleGiftToMe} />
                    <Text size='body-long' color={colors.textSecondary}>
                        {t('giftOrder.giftToMe')}
                    </Text>
                </GiftToMeCheck>
            </HeaderRow>

            {giftRecipients.length > 0 && (
                <SummaryButton type='button' aria-expanded={expanded} aria-controls='gift-recipient-list'
                    onClick={() => setExpanded(!expanded)}>
                    <Text as='span'>
                        {t('giftOrder.recipientCount', { count: giftRecipients.length })}
                    </Text>
                    {expanded ? <ArrowLineUp size={16} color={colors.gray7} />
                        : <ArrowLineDown size={16} color={colors.gray7} />}
                </SummaryButton>
            )}
            {giftRecipients.length > 0 && expanded && (
                <List id='gift-recipient-list'>
                    {giftRecipients.map((contact, i) => (
                        <RecipientRow key={`${contact.phoneNo}-${i}`}>
                            <InfoRow spacing={2}>
                                <NameText as='p' size='body-long' fontWeight='medium' color={colors.textSecondary} ellipsis>{contact.name}</NameText>
                                <PhoneText as='p' size='body-long' fontWeight='medium' color={colors.textSecondary}>{contact.phoneNo}</PhoneText>
                            </InfoRow>
                            <RemoveButton
                                type='button'
                                aria-label={t('giftOrder.delete')}
                                onClick={() => deleteRecipient(i)}>
                                <CloseInCircleIcon size={16} color={colors.gray7} />
                            </RemoveButton>
                        </RecipientRow>
                    ))}
                </List>
            )}

            <AddButton type='button' onClick={handleAddContact}>
                <PlusMark aria-hidden='true'>＋</PlusMark>{t('giftOrder.addContact').replace(/^\+\s*/, '')}
            </AddButton>

            <Notice>{t('giftOrder.recipientNotice').split('\n').map((line) => <li key={line}>{line}</li>)}</Notice>
        </Column>
    );
}

const HeaderRow = styled(Row)`
    min-height: 40px;
`;

const GiftToMeCheck = styled.label`
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    flex-shrink: 0;
    cursor: pointer;
`;

const CheckInput = styled.input`
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: ${colors.gray7};
    cursor: pointer;
`;

const SummaryButton = styled.button`
    display: flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-2) 0;
    border: 0;
    background: transparent;
    &:focus-visible { outline: 2px solid ${colors.primary}; }
`;

const List = styled.ul`
    display: flex;
    width: 100%;
    margin: 0;
    padding: var(--spacing-4);
    flex-direction: column;
    list-style: none;
    border-radius: 8px;
    background: ${colors.gray1};
`;

const InfoRow = styled(Row)`
    flex: 1;
    min-width: 0;
`;

const NameText = styled(Text)`
    width: 70px;
`;

const PhoneText = styled(Text)`
    flex-shrink: 0;
`;

const RecipientRow = styled.li`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-2);
    min-height: 17px;
    & + & { margin-top: var(--spacing-4); padding-top: var(--spacing-4); border-top: 1px solid ${colors.gray2}; }
`;

const RemoveButton = styled.button`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
`;

const AddButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-2);
    height: 50px;
    padding: 0 var(--spacing-5);
    border: 1px solid ${colors.gray2};
    border-radius: 16px;
    background: ${colors.white};
    color: ${colors.gray7};
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
`;

const PlusMark = styled.span`
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
`;

const Notice = styled.ul`
    margin: 0;
    padding-left: 18px;
    list-style: disc;
    color: ${colors.gray5};
    font-size: 12px;
    line-height: 1.4;
`;

export default Receiver;
